import {
  ArtistImage,
} from "../../pages/AddArtist/style"
import { ReactComponent as SpotifyFollowersIcon } from "assets/images/streaming/icons/spotify-i.svg"
import millify from "millify"
import { SpotifyArtistInBrandConnections, SpotifyArtistSearchResponse } from "modules/Types"
import NotFoundArtistImage from "assets/images/not-found-user.png"
import ButtonSymphony from "elements/ButtonSymphony"
import { clsx, styx } from 'modules/Utils'
import { CSSProperties } from "react"
import styled from 'styled-components'
import Sty from "modules/Sty"
import useCurrentBrand from "Hooks/useCurrentBrand"

/**
 * Gets the image URL for an artist
 * @param artist - The artist object to get the image URL from
 * @returns The image URL if found, undefined otherwise
 */
const getImageUrl = (artist?: SpotifyArtistInBrandConnections | SpotifyArtistSearchResponse | null) => {
  if (!artist) return undefined
  if ((artist as SpotifyArtistInBrandConnections).images) {
    return (artist as SpotifyArtistInBrandConnections).images[0]?.url
  } else if ((artist as SpotifyArtistSearchResponse).thumbnail_url) {
    return (artist as SpotifyArtistSearchResponse).thumbnail_url
  }

  return undefined
}

/**
 * Gets the formatted follower count for an artist
 * @param artist - The artist object to get follower count from
 * @returns Formatted follower count string
 */
const getFollowers = (artist?: SpotifyArtistInBrandConnections | SpotifyArtistSearchResponse | null) => {

  const { followers } = artist || {}

  if ((artist as SpotifyArtistInBrandConnections).followers?.total) {
    return millify((artist as SpotifyArtistInBrandConnections).followers.total)
  }
  if ((artist as SpotifyArtistSearchResponse)?.followers) {
    return millify((artist as SpotifyArtistSearchResponse).followers || 0)
  }

  return 0
}

/**
 * Props interface for ArtistItem component
 */
interface Props {
  className?: string
  style?: CSSProperties
  /** Whether the item is in a loading state */
  isLoadingSelectedArtist?: boolean
  /** Whether this artist is currently selected */
  isSelected?: boolean
  /** The artist data to display */
  artist: SpotifyArtistInBrandConnections | SpotifyArtistSearchResponse
  /** Label text for the connect button */
  buttonLabel: string
  /** Callback function when artist is selected */
  selectArtist: (artist: SpotifyArtistInBrandConnections | SpotifyArtistSearchResponse) => void
}

/**
 * ArtistItem component displays an individual artist with their image, name, 
 * follower count and connect button
 * @param props - Component props
 * @returns ArtistItem component
 */
const ArtistItem = ({
  className,
  style,
  isLoadingSelectedArtist,
  isSelected,
  artist,
  buttonLabel,
  selectArtist,
}: Props) => {
  const { isLoadingBrand } = useCurrentBrand();
  const imageUrl = getImageUrl(artist)
  const followers = getFollowers(artist)

  const handleClickItem = () => {
    if (!isLoadingSelectedArtist) {
      selectArtist(artist)
    }
  }

  return (
    <StyledArtistItem
      className={clsx('artistItemComponent', className)}
      style={styx(style)}
    >
      <ArtistInfo>
        <ArtistImage src={imageUrl || NotFoundArtistImage} />
        <ArtistName>
          <ArtistNameLabel>{artist?.name}</ArtistNameLabel>
          <ArtistFollowersContainer>
            <ArtistFollowersPlatform>
              <SpotifyFollowersIcon width={16} height={16} />
            </ArtistFollowersPlatform>
            <ArtistFollowers>
              {followers} followers
            </ArtistFollowers>
          </ArtistFollowersContainer>
        </ArtistName>
      </ArtistInfo>
      <ButtonSymphony
        className="artistItemButtonGoToDashboard"
        onClick={handleClickItem}
        disabled={isLoadingSelectedArtist || isLoadingBrand}
        variant="text"
        isLoading={isSelected && (isLoadingSelectedArtist || isLoadingBrand)}
      >
        {buttonLabel}
      </ButtonSymphony>
    </StyledArtistItem>
  )
}

const StyledArtistItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px 8px 0px;
  gap: 16px;
  height: 80px;
  width: 100%;
  border-bottom: ${Sty.border};
`;

const ArtistInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 24px;
  height: 64px;
  overflow:hidden;
`;

export const ConnectArtistLabel = styled.div`
  height: 40px;
  font-size: 16px;
  display: flex;
  align-items: center;
  text-align: right;
  color: #8800ff;
  flex: none;
  order: 1;
  flex-grow: 0;
  cursor: pointer;
`

const ArtistName = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  height: 39px;
  flex: none;
  order: 1;
  flex-grow: 0;
`

const ArtistNameLabel = styled.div`
  height: 19px;
  font-size: 16px;
  color: #000000;
`;


const ArtistFollowersContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  height: 16px;
  flex: none;
  order: 1;
  flex-grow: 0;
`

const ArtistFollowers = styled.div`
  height: 15px;
  font-size: 12px;
  display: flex;
  align-items: center;
  color: #707070;
  opacity: 0.72;
  flex: none;
  order: 1;
  flex-grow: 0;
`

export const ArtistFollowersPlatform = styled.div`
  width: 16px;
  height: 16px;
  flex: none;
  order: 0;
  flex-grow: 0;
`

export default ArtistItem
